<template>
  <Teleport to="body">
    <Transition name="cookie-slide">
      <div
        v-if="visible"
        class="cookie-banner"
        role="dialog"
        :aria-label="t('cookie.dialogLabel', 'Cookie consent')"
        aria-live="polite"
      >
        <div class="cookie-banner__inner">
          <div class="cookie-banner__text">
            <p class="cookie-banner__heading">{{ t('cookie.heading') }}</p>
            <p class="cookie-banner__body">
              {{ t('cookie.body') }}
              <a href="/cookies" class="cookie-banner__link">{{ t('cookie.moreInfo') }}</a> ·
              <a href="/privacy" class="cookie-banner__link">{{ t('cookie.privacy') }}</a>
            </p>
          </div>
          <div class="cookie-banner__actions">
            <button
              type="button"
              class="cookie-banner__btn cookie-banner__btn--primary"
              @pointerdown.stop.prevent="acceptAll"
              @click.stop.prevent="acceptAll"
            >
              {{ t('cookie.acceptAll') }}
            </button>
            <button
              type="button"
              class="cookie-banner__btn cookie-banner__btn--secondary"
              @pointerdown.stop.prevent="acceptNecessary"
              @click.stop.prevent="acceptNecessary"
            >
              {{ t('cookie.necessaryOnly') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useLanguage } from '../composables/useLanguage';

const CONSENT_KEY = 'helfer-cookie-consent';
const visible = ref(false);
const { t } = useLanguage();

onMounted(() => {
  // Never show in the Electron desktop app
  if (typeof window !== 'undefined' && (window as any).__CAFE_DESKTOP_RUNTIME__) return;

  try {
    const stored = window.localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      visible.value = true;
    }
  } catch {
    // localStorage unavailable — skip banner
  }
});

function saveConsent(analytics: boolean) {
  try {
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify({
      necessary: true,
      analytics,
      ts: Date.now(),
    }));
  } catch {
    // ignore
  }
  visible.value = false;
}

function acceptAll() {
  saveConsent(true);
}

function acceptNecessary() {
  saveConsent(false);
}
</script>

<style scoped>
.cookie-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2147483647;
  pointer-events: auto;
  padding: 0.75rem 1rem;
  background: rgba(4, 9, 19, 0.97);
  border-top: 1px solid rgba(103, 232, 249, 0.14);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 -8px 40px rgba(2, 6, 23, 0.6);
}

.cookie-banner__inner {
  max-width: 72rem;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  flex-wrap: wrap;
  pointer-events: auto;
}

.cookie-banner__text {
  flex: 1;
  min-width: 0;
}

.cookie-banner__heading {
  font-size: 0.82rem;
  font-weight: 600;
  color: rgb(226 232 240);
  margin-bottom: 0.15rem;
}

.cookie-banner__body {
  font-size: 0.78rem;
  color: rgba(148, 163, 184, 0.85);
  line-height: 1.5;
}

.cookie-banner__link {
  color: rgba(103, 232, 249, 0.75);
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color 0.15s;
}
.cookie-banner__link:hover {
  color: rgba(103, 232, 249, 1);
}

.cookie-banner__actions {
  display: flex;
  gap: 0.6rem;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.cookie-banner__btn {
  border-radius: 0.65rem;
  padding: 0.45rem 1rem;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
  border: none;
  outline: none;
  pointer-events: auto;
  touch-action: manipulation;
}

.cookie-banner__btn--primary {
  background: rgba(103, 232, 249, 0.15);
  color: rgba(103, 232, 249, 0.95);
  ring: 1px solid rgba(103, 232, 249, 0.25);
  box-shadow: 0 0 0 1px rgba(103, 232, 249, 0.22);
}
.cookie-banner__btn--primary:hover {
  background: rgba(103, 232, 249, 0.25);
  color: rgb(103, 232, 249);
}

.cookie-banner__btn--secondary {
  background: rgba(255, 255, 255, 0.05);
  color: rgba(148, 163, 184, 0.85);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.08);
}
.cookie-banner__btn--secondary:hover {
  background: rgba(255, 255, 255, 0.09);
  color: rgba(226, 232, 240, 0.9);
}

/* Slide-up transition */
.cookie-slide-enter-active {
  transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.25s ease;
}
.cookie-slide-leave-active {
  transition: transform 0.22s ease-in, opacity 0.2s ease-in;
}
.cookie-slide-enter-from {
  transform: translateY(100%);
  opacity: 0;
}
.cookie-slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
